import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeUp, fadeUpSmall } from '../lib/motion';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // Set when the backend tells us the account exists but isn't verified yet.
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverifiedEmail(null); // clear any previous banner
    try {
      await login(email, password);
      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      // Backend returns 403 + requiresVerification when the account exists
      // but the user hasn't entered their OTP yet.
      if (error.response?.data?.requiresVerification) {
        setUnverifiedEmail(error.response.data.email || email);
      } else {
        toast.error(error.response?.data?.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">

        {/* Header */}
        <motion.div
          className="mb-8 text-center"
          initial="hidden"
          animate="show"
          variants={fadeUp}
        >
          <motion.div
            className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 rounded-xl mb-4"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="text-white text-xl">🖨️</span>
          </motion.div>
          <h1 className="text-2xl font-semibold text-gray-900">Welcome back</h1>
          <p className="text-sm text-gray-500 mt-1">Sign in to manage your service requests</p>
        </motion.div>

        {/* Card */}
        <motion.div
          className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 flex flex-col gap-4"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">

            {/* Unverified account banner */}
            <AnimatePresence>
              {unverifiedEmail && (
                <motion.div
                  initial={{ opacity: 0, y: -8, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -8, height: 0 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex flex-col gap-1">
                    <p className="text-sm font-medium text-amber-800">Email not yet verified</p>
                    <p className="text-xs text-amber-700">
                      Please verify your email before logging in.{' '}
                      <Link
                        to="/signup"
                        state={{ step: 'verify', email: unverifiedEmail }}
                        className="font-semibold underline hover:text-amber-900 transition"
                      >
                        Enter your code →
                      </Link>
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Email */}
            <motion.div className="flex flex-col gap-1" variants={fadeUpSmall} initial="hidden" animate="show" transition={{ delay: 0.15 }}>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Email <span className="text-orange-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
            </motion.div>

            {/* Password */}
            <motion.div className="flex flex-col gap-1" variants={fadeUpSmall} initial="hidden" animate="show" transition={{ delay: 0.2 }}>
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Password <span className="text-orange-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="text-xs text-blue-500 hover:text-blue-700 transition"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-xs text-blue-500 hover:text-blue-700 hover:underline transition"
                >
                  Forgot password?
                </Link>
              </div>
            </motion.div>

            {/* Submit */}
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-3 rounded-xl transition-colors mt-1"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </motion.button>

          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-1">
            <div className="flex-1 h-px bg-gray-100" />
            <span className="text-xs text-gray-400">or</span>
            <div className="flex-1 h-px bg-gray-100" />
          </div>

          {/* New account links */}
          <p className="text-center text-sm text-gray-500">
            New here?{' '}
            <Link to="/signup" className="text-blue-600 font-medium hover:text-blue-700 transition">
              Create an account
            </Link>
          </p>
          <p className="text-center text-xs text-gray-400">
            Want to work as an engineer?{' '}
            <Link to="/apply-engineer" className="text-blue-600 hover:underline">
              Apply here
            </Link>
          </p>

        </motion.div>
      </div>
    </div>
  );
};

export default Login;